import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';

import { DataService } from 'src/app/dashboard/services/data.service';
import { TableDataExportService } from 'src/app/dashboard/services/table-data-export.service';

import { DateFilterComponent } from '../../../pages/details/details-page/date-filter/date-filter.component';

interface DataTarget {
  id: string;
  name: string;
  collection: {
    name: string;
    isDaily: boolean;
    hasComparison: boolean;
  };
  willDownload: boolean;
}
@Component({
  selector: 'app-download-dialog',
  templateUrl: './download-dialog.component.html',
  styleUrls: ['./download-dialog.component.scss'],
  imports: [DateFilterComponent],
})
export class DownloadDialogComponent {
  fileType: 'csv' | 'xlsx' = 'csv';
  fileName = 'stroly-map-data'; // TODO: map idを反映する
  fromDateStr = '';
  toDateStr = '';
  dataTargets: DataTarget[] = [];

  labels = {
    title: '',
    targetDuration: '',
    targetData: '',
    checkAll: '',
    uncheckAll: '',
    fileFormat: '',
    download: '',
    cancel: '',
    access: '',
    spotViewsTotal: '',
    spotViewsRanking: '',
    referrer: '',
    accessFromCampaign: '',
    usersLanguage: '',
    usersArea: '',
    usersDevice: '',
    usersOs: '',
  };

  private tableDataExportService = inject(TableDataExportService);
  private dataService = inject(DataService);
  private translate = inject(TranslateService);
  private dialogRef = inject(MatDialog);

  constructor() {
    this.updateDateStr();
    this.initLabels();
    this.initDataTargets();
  }

  private updateDateStr(): void {
    this.fromDateStr = this.dataService.getFromDate();
    this.toDateStr = this.dataService.getToDate();
  }

  private initLabels(): void {
    this.translate
      .get([
        'downloadDialog.title',
        'downloadDialog.targetDuration',
        'downloadDialog.targetData',
        'downloadDialog.checkAll',
        'downloadDialog.uncheckAll',
        'downloadDialog.fileFormat',
        'downloadDialog.download',
        'downloadDialog.cancel',
        'downloadDialog.access',
        'downloadDialog.spotViewsTotal',
        'downloadDialog.spotViewsRanking',
        'downloadDialog.referrer',
        'downloadDialog.accessFromCampaign',
        'downloadDialog.usersLanguage',
        'downloadDialog.usersArea',
        'downloadDialog.usersDevice',
        'downloadDialog.usersOs',
      ])
      .subscribe((translations) => {
        this.labels.title = translations['downloadDialog.title'];
        this.labels.targetDuration =
          translations['downloadDialog.targetDuration'];
        this.labels.targetData = translations['downloadDialog.targetData'];
        this.labels.checkAll = translations['downloadDialog.checkAll'];
        this.labels.uncheckAll = translations['downloadDialog.uncheckAll'];
        this.labels.fileFormat = translations['downloadDialog.fileFormat'];
        this.labels.download = translations['downloadDialog.download'];
        this.labels.cancel = translations['downloadDialog.cancel'];
        this.labels.access = translations['downloadDialog.access'];
        this.labels.spotViewsTotal =
          translations['downloadDialog.spotViewsTotal'];
        this.labels.spotViewsRanking =
          translations['downloadDialog.spotViewsRanking'];
        this.labels.referrer = translations['downloadDialog.referrer'];
        this.labels.accessFromCampaign =
          translations['downloadDialog.accessFromCampaign'];
        this.labels.usersLanguage =
          translations['downloadDialog.usersLanguage'];
        this.labels.usersArea = translations['downloadDialog.usersArea'];
        this.labels.usersDevice = translations['downloadDialog.usersDevice'];
        this.labels.usersOs = translations['downloadDialog.usersOs'];
      });
  }

  private initDataTargets(): void {
    this.dataTargets = [
      // type 1: 日ごとの集計
      {
        id: 'access',
        name: this.labels.access,
        collection: { name: 'v1miaccess', isDaily: true, hasComparison: true },
        willDownload: true,
      },
      {
        id: 'spot-views-total',
        name: this.labels.spotViewsTotal,
        collection: { name: 'v1landmark', isDaily: true, hasComparison: true },
        willDownload: true,
      },
      // type 2: 期間中の総数の集計
      {
        id: 'spot-views-ranking',
        name: this.labels.spotViewsRanking,
        collection: {
          name: 'v2spots',
          isDaily: false,
          hasComparison: false,
        },
        willDownload: true,
      },
      {
        id: 'acccess-referrers',
        name: this.labels.referrer,
        collection: {
          name: 'v1mireferrer',
          isDaily: false,
          hasComparison: false,
        },
        willDownload: true,
      },
      {
        id: 'acccess-inflow-from-campaigns',
        name: this.labels.accessFromCampaign,
        collection: {
          name: 'v2micampaign',
          isDaily: false,
          hasComparison: false,
        },
        willDownload: true,
      },
      {
        id: 'users-languages',
        name: this.labels.usersLanguage,
        collection: { name: 'v1milang', isDaily: false, hasComparison: false },
        willDownload: true,
      },
      {
        id: 'users-region-country-of-origin',
        name: this.labels.usersArea,
        collection: { name: 'v1miarea', isDaily: false, hasComparison: false },
        willDownload: true,
      },
      {
        id: 'users-devices',
        name: this.labels.usersDevice,
        collection: { name: 'v1model', isDaily: false, hasComparison: false },
        willDownload: true,
      },
      {
        id: 'users-os',
        name: this.labels.usersOs,
        collection: { name: 'v1os', isDaily: false, hasComparison: false },
        willDownload: true,
      },
    ];
  }

  // すべて選択/解除
  onToggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement)?.checked ?? false;
    for (const target of this.dataTargets) {
      target.willDownload = checked;
    }
  }

  // 全部がチェック済みかどうかを判定
  isAllChecked(): boolean {
    return this.dataTargets.every((target) => target.willDownload);
  }

  onChangeFileType(event: Event): void {
    const fileType = (event.target as HTMLSelectElement)?.value ?? 'csv';
    this.fileType = fileType as 'csv' | 'xlsx';
  }

  onToggleDataTarget(event: Event, dataTarget: DataTarget): void {
    const checked = (event.target as HTMLInputElement)?.checked ?? false;
    dataTarget.willDownload = checked;
  }

  download(): void {
    // console.log('download');
    const targets = this.dataTargets.filter((target) => target.willDownload);
    const collections = targets.map((target) => ({
      ...target.collection,
      sheetName: target.name,
    }));

    this.updateDateStr();
    this.fileName = `stroly-map-data_${this.fromDateStr}-${this.toDateStr}`;

    this.tableDataExportService.exportData(
      collections,
      this.fileName,
      this.fileType,
    );
  }

  toggleDownloadDialog(): void {
    this.dialogRef.closeAll();
  }
}
